import { useEffect, useState } from "react";
import { useT, type MsgKey } from "../../lib/i18n";
import { colorOf, findField, GHOST_MS, type CollabGhost, type CollabPeer } from "../../lib/collab";
import { useCollabCtx } from "./CollabContext";
import { useChangeLabels } from "./changeLabels";
import { AGENT_FREE_PAGES } from "../../lib/collab-view";
import { CursorParty } from "./CursorParty";

/** 页面 id → 菜单上的叫法 */
function usePageName() {
  const t = useT();
  return (page: string) => { const k = `nav.${page}`; const s = t(k as MsgKey); return s === k ? page : s; };
}

/**
 * 顶栏：这个代理下谁在线（设计稿 nav.jsx OnlineBar）。悬停看他在哪个版本、哪一页、在干什么；
 * 点头像跳到他那里，卡片里「跟随」之后他走到哪我跟到哪。空闲的人头像变灰、排在后面
 */
/** 离开（页面在后台）或空闲：头像变灰、排到后面 */
const inactive = (p: CollabPeer) => p.away || p.idleMin != null;

export function OnlineBar() {
  const t = useT();
  const pageName = usePageName();
  const { peers, connected, agent, agentLabel, jumpTo, following, setFollowing, showCursors, setShowCursors } = useCollabCtx();
  const [tip, setTip] = useState<CollabPeer | null>(null);
  /** 全部在线的人的列表：停在「在线 N」或「+N」上打开（头像只放得下 5 个） */
  const [list, setList] = useState(false);
  if (!peers.length) return connected ? <span className="flex-none text-xs text-ink-placeholder">{t("collab.onlyYou")}</span> : null;
  const sorted = [...peers].sort((a, b) => (inactive(a) ? 1 : 0) - (inactive(b) ? 1 : 0));
  const shown = sorted.slice(0, 5), more = sorted.length - shown.length;
  const doing = (p: CollabPeer) => (p.away ? t("collab.away") : p.idleMin != null ? t("collab.idle", { n: p.idleMin }) : p.field ? t("collab.filling", { label: p.field.label || "—" }) : t("collab.viewing"));
  return (
    <span className="relative flex flex-none items-center gap-1.5" onMouseLeave={() => { setTip(null); setList(false); }}>
      <button type="button" onMouseEnter={() => { setTip(null); setList(true); }} onClick={() => setList(!list)} aria-expanded={list}
        className="text-xs text-ink-secondary hover:text-primary">{t("collab.online", { n: peers.length })}</button>
      <span className="inline-flex">
        {shown.map((p, i) => (
          <button key={p.id} type="button" onMouseEnter={() => { setList(false); setTip(p); }} onClick={() => jumpTo(p)} aria-label={t("collab.jumpTo", { who: p.name })}
            style={{ background: colorOf(p.name), borderColor: following === p.id ? colorOf(p.name) : "#fff", opacity: inactive(p) ? 0.4 : 1, filter: inactive(p) ? "grayscale(1)" : "none" }}
            className={`inline-flex h-[26px] w-[26px] items-center justify-center rounded-full border-2 text-[11px] font-semibold text-white ${i ? "-ml-1.5" : ""}`}>
            {p.name.slice(0, 1).toUpperCase()}
          </button>
        ))}
      </span>
      {more > 0 && (
        <button type="button" onMouseEnter={() => { setTip(null); setList(true); }} onClick={() => setList(!list)} aria-expanded={list}
          className="text-xs text-ink-secondary hover:text-primary">+{more}</button>
      )}
      {/* 关掉就不画别人的鼠标（含离线留下的），描格照常 */}
      <button type="button" onClick={() => setShowCursors(!showCursors)} aria-pressed={!showCursors}
        title={t(showCursors ? "collab.hideCursors" : "collab.showCursors")} aria-label={t(showCursors ? "collab.hideCursors" : "collab.showCursors")}
        className={`inline-flex h-[22px] w-[22px] items-center justify-center rounded-sm hover:bg-fill-light ${showCursors ? "text-ink-secondary" : "text-ink-placeholder"}`}>
        <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden>
          <path d="M2 2l5 12 1.8-5.2L14 7z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
          {!showCursors && <path d="M1.5 14.5l13-13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />}
        </svg>
      </button>
      {list && (
        // 同悬浮卡：贴着下沿开始，鼠标往下移进列表不会先离开。人多时列表自己滚
        <span className="absolute right-0 top-full z-50 pt-1.5">
          <span className="block max-h-[60vh] w-max min-w-[260px] max-w-[480px] overflow-y-auto rounded-base border border-line-lighter bg-white py-1 text-sm shadow-light">
            {sorted.map((p) => (
              <span key={p.id} className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-fill-lighter">
                <button type="button" onClick={() => { jumpTo(p); setList(false); }} aria-label={t("collab.jumpTo", { who: p.name })} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                  <span style={{ background: colorOf(p.name), opacity: inactive(p) ? 0.4 : 1, filter: inactive(p) ? "grayscale(1)" : "none" }}
                    className="inline-flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full text-[11px] font-semibold text-white">{p.name.slice(0, 1).toUpperCase()}</span>
                  <span className="min-w-0">
                    <b style={{ color: colorOf(p.name) }}>{p.name}</b>
                    <span className="block truncate text-xs text-ink-secondary">
                      {p.a0 && p.a0 !== agent ? `${t("collab.inAgent", { agent: agentLabel(p.a0) })} · ` : ""}{pageName(p.page)}{p.version != null ? ` · v${p.version}` : ""} · {doing(p)}
                    </span>
                  </span>
                </button>
                <button type="button" className="flex-none rounded-sm border border-line px-1.5 py-0.5 text-xs text-ink-regular hover:border-primary hover:text-primary"
                  onClick={() => { setFollowing(following === p.id ? null : p.id); if (following !== p.id) jumpTo(p); setList(false); }}>
                  {following === p.id ? t("collab.unfollow") : t("collab.follow")}
                </button>
              </span>
            ))}
          </span>
        </span>
      )}
      {tip && (
        // 卡片贴着头像下沿开始（外层 top-full + pt-1.5 补上空隙），鼠标从头像往下移到「跟随」不会先离开、卡片不会消失
        <span className="absolute right-0 top-full z-50 pt-1.5">
        <span className="block whitespace-nowrap rounded-base border border-line-lighter bg-white px-2.5 py-1.5 text-sm shadow-light">
          <b style={{ color: colorOf(tip.name) }}>{tip.name}</b>{tip.a0 && tip.a0 !== agent ? <> · {t("collab.inAgent", { agent: agentLabel(tip.a0) })}</> : null} · {pageName(tip.page)}{tip.version != null ? ` · v${tip.version}` : ""} · {doing(tip)}
          <span className="mt-1 flex items-center gap-2 text-xs text-ink-placeholder">
            {t("collab.jumpHint")}
            <button type="button" className="rounded-sm border border-line px-1.5 py-0.5 text-ink-regular hover:border-primary hover:text-primary"
              onClick={() => { setFollowing(following === tip.id ? null : tip.id); if (following !== tip.id) jumpTo(tip); }}>
              {following === tip.id ? t("collab.unfollow") : t("collab.follow")}
            </button>
          </span>
        </span>
        </span>
      )}
    </span>
  );
}

/** 左侧菜单：谁在这一页（他颜色的小点，最多 3 个） */
export function PageDots({ page }: { page: string }) {
  const t = useT();
  const { peers, agent } = useCollabCtx();
  // 菜单是当前代理的：别的代理里同名页面上的人不算在这一页（课程、账号这些跟代理无关的页除外）
  const here = peers.filter((p) => p.page === page && (p.a0 === agent || AGENT_FREE_PAGES.includes(page)));
  if (!here.length) return null;
  return (
    <span className="ml-auto inline-flex flex-none items-center gap-0.5" title={t("collab.here", { names: here.map((p) => p.name).join("、") })}>
      {here.slice(0, 3).map((p) => <span key={p.id} className="h-1.5 w-1.5 rounded-full" style={{ background: colorOf(p.name), opacity: inactive(p) ? 0.4 : 1 }} />)}
      {here.length > 3 && <span className="text-[10px] text-ink-placeholder">+{here.length - 3}</span>}
    </span>
  );
}

/** 收起的菜单分组：组里任何一页有人就在组标题上点出来，不然收起后协同的人从菜单上消失 */
export function GroupDots({ pages }: { pages: string[] }) {
  const t = useT();
  const { peers, agent } = useCollabCtx();
  const here = peers.filter((p) => pages.includes(p.page) && (p.a0 === agent || AGENT_FREE_PAGES.includes(p.page)));
  if (!here.length) return null;
  return (
    <span className="ml-auto inline-flex flex-none items-center gap-0.5" title={t("collab.here", { names: here.map((p) => p.name).join("、") })}>
      {here.slice(0, 3).map((p) => <span key={p.id} className="h-1.5 w-1.5 rounded-full" style={{ background: colorOf(p.name), opacity: inactive(p) ? 0.4 : 1 }} />)}
      {here.length > 3 && <span className="text-[10px] text-ink-placeholder">+{here.length - 3}</span>}
    </span>
  );
}

/** 跟随中：顶上一条他颜色的色带，说清在跟谁、怎么停 */
export function FollowBanner({ peer, onStop }: { peer: CollabPeer; onStop: () => void }) {
  const t = useT();
  const pageName = usePageName();
  const { agent, agentLabel } = useCollabCtx();
  return (
    <div role="status" className="fixed inset-x-0 top-0 z-[80] flex h-[26px] items-center justify-center gap-3 text-sm text-white" style={{ background: colorOf(peer.name) }}>
      {t("collab.following", { who: peer.name, where: `${peer.a0 && peer.a0 !== agent ? `${t("collab.inAgent", { agent: agentLabel(peer.a0) })} · ` : ""}${peer.version != null ? `v${peer.version} · ` : ""}${pageName(peer.page)}` })}
      <button type="button" className="underline" onClick={onStop}>{t("collab.stop")}</button>
    </div>
  );
}

/**
 * 别人的鼠标与在填哪格（设计稿 nav.jsx PresenceLayer / FieldPresence）。
 * 固定定位盖在最上层、不挡点击；位置按主内容区换算，滚动、窗口大小变了跟着走。
 * 弹窗里的输入框也能描（弹窗在 body 下，同样标了 data-collab-root）。
 * 鼠标碰到一起有个小彩蛋（CursorParty）；关了「显示别人的鼠标」就不挂，碰不到也不放。
 */
export function CollabLayer({ peers, ghosts, page, showCursors, me }: { peers: CollabPeer[]; ghosts: CollabGhost[]; page: string; showCursors: boolean; me: string }) {
  const t = useT();
  const [, tick] = useState(0);
  const here = peers.filter((p) => p.page === page);
  // 离线的人：鼠标留在最后的位置，半透明（30 分钟没动过的不再画）
  const left = showCursors ? ghosts.filter((g) => g.page === page && Date.now() - g.at < GHOST_MS) : [];
  const count = here.length + left.length;
  useEffect(() => {
    if (!count) return;
    // 别人的输入框位置会随我的滚动、展开收起变化：画的东西少，300ms 重新量一次足够
    const id = setInterval(() => tick((x) => x + 1), 300);
    const re = () => tick((x) => x + 1);
    window.addEventListener("scroll", re, true);
    window.addEventListener("resize", re);
    return () => { clearInterval(id); window.removeEventListener("scroll", re, true); window.removeEventListener("resize", re); };
  }, [count]);
  if (!count) return null;
  const main = document.querySelector("[data-collab-main]")?.getBoundingClientRect();
  return (
    <div className="pointer-events-none fixed inset-0 z-[70]">
      {showCursors && here.length > 0 && <CursorParty peers={here} me={me} />}
      {here.map((p) => {
        const color = colorOf(p.name);
        const el = p.field ? findField(p.field) : null;
        const box = el?.getBoundingClientRect();
        return (
          <div key={p.id}>
            {box && box.width > 0 && (
              <div className="absolute rounded-base" style={{ left: box.left - 2, top: box.top - 2, width: box.width + 4, height: box.height + 4, boxShadow: `0 0 0 2px ${color}` }}>
                <span className="absolute -top-4 right-0 whitespace-nowrap rounded-sm px-1 text-[10px] leading-[15px] text-white" style={{ background: color }}>{p.name}</span>
              </div>
            )}
            {showCursors && p.cursor && main && (
              <div data-cursor-id={p.id} className="absolute transition-[left,top] duration-200 ease-linear" style={{ left: main.left + p.cursor.x * main.width, top: main.top + p.cursor.y * main.height }}>
                <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden><path d="M1 1l5.5 14 2-6 6-2z" fill={color} stroke="#fff" strokeWidth="1.2" /></svg>
                <span className="ml-2.5 whitespace-nowrap rounded px-1.5 text-[11px] text-white" style={{ background: color }}>{p.name}</span>
              </div>
            )}
          </div>
        );
      })}
      {main && left.map((g) => {
        const color = colorOf(g.name);
        return (
          <div key={`gone-${g.id}`} className="absolute" style={{ opacity: 0.45, left: main.left + g.cursor.x * main.width, top: main.top + g.cursor.y * main.height }}>
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden><path d="M1 1l5.5 14 2-6 6-2z" fill={color} stroke="#fff" strokeWidth="1.2" /></svg>
            <span className="ml-2.5 whitespace-nowrap rounded px-1.5 text-[11px] text-white" style={{ background: color }}>{g.name} · {t("collab.offline")}</span>
          </div>
        );
      })}
    </div>
  );
}

/** 别人存了：他改的块已经合进来，右下角提示几秒（设计稿 nav.jsx MergedNotice） */
export function MergedNotice({ notice, onDone }: { notice: { by: string; summary: string; conflicts: string[] } | null; onDone: () => void }) {
  const t = useT();
  const { where, field } = useChangeLabels();
  useEffect(() => { if (!notice) return; const id = setTimeout(onDone, 6000); return () => clearTimeout(id); }, [notice, onDone]);
  if (!notice) return null;
  const color = colorOf(notice.by);
  return (
    <div role="status" className="fixed bottom-5 right-5 z-[66] rounded-base border border-line-lighter bg-white px-3 py-2 text-sm shadow-light" style={{ borderLeft: `3px solid ${color}` }}>
      <b style={{ color }}>{notice.by}</b> {t("collab.merged", { what: notice.summary || "—" })}
      {/* 两个人改了同一个字段：留的是我的，说清楚是哪几处 */}
      {notice.conflicts.length > 0 && (
        <div className="mt-1 text-xs text-warning-700">
          {t("collab.conflicts", {
            who: notice.by,
            where: notice.conflicts.slice(0, 3).map((p) => {
              const m = /^([^.[]+)(.*)$/.exec(p);
              const c = { block: m?.[1] ?? p, path: (m?.[2] ?? "").replace(/^\./, "") };
              return `${where(c)} · ${field(c)}`;
            }).join("；") + (notice.conflicts.length > 3 ? ` …` : ""),
          })}
        </div>
      )}
    </div>
  );
}
